import { BudgetCategory } from '../types/ai-budget'

/**
 * Category Mapping Service
 * Maps transaction categories to budget categories using intelligent fuzzy matching
 */

interface CategoryMapping {
  transactionCategory: string
  budgetCategory: BudgetCategory | null
  confidence: number
}

interface MappingRule {
  transactionCategory: string
  keywords: string[]
  type?: 'essential' | 'discretionary' | 'savings' | 'investment'
}

class CategoryMappingService {
  private mappingRules: MappingRule[] = [
    // Food & Dining
    {
      transactionCategory: 'food',
      keywords: ['food', 'groceries', 'dining', 'meals', 'restaurant', 'grocery'],
      type: 'essential'
    },
    
    // Transportation
    {
      transactionCategory: 'transportation',
      keywords: ['transportation', 'transport', 'car', 'gas', 'fuel', 'parking', 'uber', 'lyft', 'taxi'],
      type: 'essential'
    },
    
    // Entertainment
    {
      transactionCategory: 'entertainment',
      keywords: ['entertainment', 'movies', 'games', 'streaming', 'netflix', 'hulu', 'spotify', 'fun'],
      type: 'discretionary'
    },
    
    // Shopping
    {
      transactionCategory: 'shopping',
      keywords: ['shopping', 'retail', 'clothes', 'clothing', 'amazon', 'discretionary'],
      type: 'discretionary'
    },
    
    // Bills & Utilities
    {
      transactionCategory: 'bills',
      keywords: ['bills', 'utilities', 'housing', 'rent', 'mortgage', 'electric', 'water', 'internet', 'phone'],
      type: 'essential'
    },
    
    // Healthcare
    {
      transactionCategory: 'healthcare',
      keywords: ['healthcare', 'health', 'medical', 'doctor', 'pharmacy', 'insurance'],
      type: 'essential'
    },
    
    // Education
    {
      transactionCategory: 'education',
      keywords: ['education', 'school', 'tuition', 'books', 'learning', 'course'],
      type: 'essential'
    },
    
    // Travel
    {
      transactionCategory: 'travel',
      keywords: ['travel', 'vacation', 'hotel', 'flight', 'trip'],
      type: 'discretionary'
    },
    
    // Personal Care
    {
      transactionCategory: 'personal',
      keywords: ['personal', 'care', 'hygiene', 'beauty', 'gym', 'fitness'],
      type: 'discretionary'
    },
    
    // Investment/Savings (for income transactions)
    {
      transactionCategory: 'investment',
      keywords: ['investment', 'savings', 'emergency', 'retirement', '401k', 'ira', 'portfolio'],
      type: 'investment'
    },
    
    // Salary (for income transactions)
    {
      transactionCategory: 'salary',
      keywords: ['salary', 'income', 'wages', 'payroll'],
      type: 'essential'
    }
  ]

  /**
   * Map a transaction category to the best matching budget category
   */
  mapTransactionToBudgetCategory(
    transactionCategory: string,
    budgetCategories: BudgetCategory[]
  ): BudgetCategory | null {
    console.log('🔍 CategoryMappingService: Mapping transaction category:', transactionCategory)
    console.log('🔍 CategoryMappingService: Available budget categories:', budgetCategories.map(c => c.name))

    if (!transactionCategory || !budgetCategories.length) {
      console.log('⚠️ CategoryMappingService: Missing transaction category or budget categories')
      return null
    }

    // Step 1: Exact match (case-insensitive)
    const exactMatch = budgetCategories.find(
      budgetCat => budgetCat.name.toLowerCase() === transactionCategory.toLowerCase()
    )
    if (exactMatch) {
      console.log('✅ CategoryMappingService: Exact match found:', exactMatch.name)
      return exactMatch
    }

    // Step 2: Find mapping rule for transaction category
    const mappingRule = this.mappingRules.find(
      rule => rule.transactionCategory === transactionCategory
    )

    if (!mappingRule) {
      console.log('⚠️ CategoryMappingService: No mapping rule found for:', transactionCategory)
      return this.findFallbackCategory(budgetCategories, transactionCategory)
    }

    // Step 3: Contains match using keywords
    const containsMatch = budgetCategories.find(budgetCat => {
      const budgetName = budgetCat.name.toLowerCase()
      return mappingRule.keywords.some(keyword => 
        budgetName.includes(keyword.toLowerCase())
      )
    })

    if (containsMatch) {
      console.log('✅ CategoryMappingService: Contains match found:', containsMatch.name)
      return containsMatch
    }

    // Step 4: Type-based fallback
    if (mappingRule.type) {
      const typeMatch = budgetCategories.find(
        budgetCat => budgetCat.type === mappingRule.type
      )
      if (typeMatch) {
        console.log('✅ CategoryMappingService: Type-based match found:', typeMatch.name)
        return typeMatch
      }
    }

    // Step 5: Final fallback
    console.log('⚠️ CategoryMappingService: No match found, using fallback')
    return this.findFallbackCategory(budgetCategories, transactionCategory)
  }

  /**
   * Find fallback category when no specific match is found
   */
  private findFallbackCategory(
    budgetCategories: BudgetCategory[],
    transactionCategory: string
  ): BudgetCategory | null {
    // For expense transactions, try to find a "discretionary" or "other" category
    if (['entertainment', 'shopping', 'travel', 'personal'].includes(transactionCategory)) {
      const discretionary = budgetCategories.find(cat => 
        cat.type === 'discretionary' || 
        cat.name.toLowerCase().includes('discretionary') ||
        cat.name.toLowerCase().includes('personal')
      )
      if (discretionary) {
        console.log('🔄 CategoryMappingService: Using discretionary fallback:', discretionary.name)
        return discretionary
      }
    }

    // For essential transactions, try to find an "essential" category
    if (['food', 'transportation', 'bills', 'healthcare', 'education'].includes(transactionCategory)) {
      const essential = budgetCategories.find(cat => 
        cat.type === 'essential' ||
        cat.name.toLowerCase().includes('essential') ||
        cat.name.toLowerCase().includes('living')
      )
      if (essential) {
        console.log('🔄 CategoryMappingService: Using essential fallback:', essential.name)
        return essential
      }
    }

    // Final fallback: return the first category (as per requirement #2b - handle gracefully)
    if (budgetCategories.length > 0) {
      console.log('🔄 CategoryMappingService: Using first available category as fallback:', budgetCategories[0].name)
      return budgetCategories[0]
    }

    console.log('❌ CategoryMappingService: No fallback category available')
    return null
  }

  /**
   * Get all possible mappings for a transaction category
   */
  getAllPossibleMappings(
    transactionCategory: string,
    budgetCategories: BudgetCategory[]
  ): CategoryMapping[] {
    const mappings: CategoryMapping[] = []

    const mappingRule = this.mappingRules.find(
      rule => rule.transactionCategory === transactionCategory
    )

    if (!mappingRule) {
      return mappings
    }

    budgetCategories.forEach(budgetCat => {
      const budgetName = budgetCat.name.toLowerCase()
      let confidence = 0

      // Exact match
      if (budgetName === transactionCategory.toLowerCase()) {
        confidence = 1.0
      }
      // Contains keyword match
      else if (mappingRule.keywords.some(keyword => 
        budgetName.includes(keyword.toLowerCase())
      )) {
        confidence = 0.8
      }
      // Type match
      else if (mappingRule.type && budgetCat.type === mappingRule.type) {
        confidence = 0.6
      }

      if (confidence > 0) {
        mappings.push({
          transactionCategory,
          budgetCategory: budgetCat,
          confidence
        })
      }
    })

    return mappings.sort((a, b) => b.confidence - a.confidence)
  }

  /**
   * Validate that a mapping makes sense
   */
  validateMapping(
    transactionCategory: string,
    budgetCategory: BudgetCategory
  ): { isValid: boolean; reason?: string } {
    // Basic validation
    if (!transactionCategory || !budgetCategory) {
      return { isValid: false, reason: 'Missing category data' }
    }

    // Check if this is a reasonable mapping based on common sense
    const transactionType = this.getTransactionType(transactionCategory)
    const budgetType = budgetCategory.type

    // Income transactions shouldn't map to expense budget categories
    if (transactionType === 'income' && budgetType !== 'investment') {
      return { 
        isValid: false, 
        reason: 'Income transactions should not map to expense budget categories' 
      }
    }

    // Expense transactions shouldn't map to savings/investment categories (usually)
    if (transactionType === 'expense' && budgetType === 'savings') {
      return { 
        isValid: false, 
        reason: 'Expense transactions should not map to savings budget categories' 
      }
    }

    return { isValid: true }
  }

  /**
   * Get the type of a transaction category
   */
  private getTransactionType(category: string): 'income' | 'expense' {
    const incomeCategories = ['salary', 'freelance', 'investment', 'gift', 'allowance']
    return incomeCategories.includes(category) ? 'income' : 'expense'
  }

  /**
   * Get mapping statistics for debugging
   */
  getMappingStats(
    transactions: Array<{ category?: string }>,
    budgetCategories: BudgetCategory[]
  ): {
    totalTransactions: number
    mappedTransactions: number
    unmappedTransactions: number
    mappingRate: number
    categoryBreakdown: Record<string, number>
  } {
    const stats = {
      totalTransactions: transactions.length,
      mappedTransactions: 0,
      unmappedTransactions: 0,
      mappingRate: 0,
      categoryBreakdown: {} as Record<string, number>
    }

    transactions.forEach(transaction => {
      if (!transaction.category) return

      stats.categoryBreakdown[transaction.category] = 
        (stats.categoryBreakdown[transaction.category] || 0) + 1

      const mappedCategory = this.mapTransactionToBudgetCategory(
        transaction.category,
        budgetCategories
      )

      if (mappedCategory) {
        stats.mappedTransactions++
      } else {
        stats.unmappedTransactions++
      }
    })

    stats.mappingRate = stats.totalTransactions > 0 
      ? (stats.mappedTransactions / stats.totalTransactions) * 100 
      : 0

    return stats
  }
}

// Create and export singleton instance
export const categoryMappingService = new CategoryMappingService()

// Export the class for testing
export default CategoryMappingService
